/**
 * Composant bouton de navigation directionnel
 * 4 variantes: up, down, left, right
 */

export class NavButton {
  constructor(direction, text, href, options = {}) {
    this.direction = direction;
    this.text = text;
    this.href = href;
    this.options = {
      position: options.position || 'center',
      className: options.className || '',
      ariaLabel: options.ariaLabel || `${text} - ${direction}`,
      ...options
    };
  }

  // Configuration des triangles SVG et alignements par direction
  static getDirectionConfig(direction) {
    const configs = {
      up: {
        chevron: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="12,6 18,16 6,16"/></svg>`,
        textOrder: 'chevron-first', // chevron puis texte
        alignment: 'flex-row', // horizontal pour haut
        defaultPosition: 'top-2 left-[80%]',
        sessionStorageDir: 'up',
        textRotation: ''
      },
      down: {
        chevron: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="12,18 6,8 18,8"/></svg>`,
        textOrder: 'text-first', // texte puis chevron
        alignment: 'flex-row', // horizontal pour bas
        defaultPosition: 'bottom-2 left-[70%]',
        sessionStorageDir: 'down',
        textRotation: ''
      },
      left: {
        chevron: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="6,12 16,6 16,18"/></svg>`,
        textOrder: 'chevron-first', // chevron puis texte
        alignment: 'flex-col', // vertical pour gauche
        defaultPosition: 'top-[60%] left-1 -translate-y-1/2',
        sessionStorageDir: 'left',
        textRotation: '',
        useWritingMode: true
      },
      right: {
        chevron: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="18,12 8,18 8,6"/></svg>`,
        textOrder: 'text-first', // texte puis chevron
        alignment: 'flex-col', // vertical pour droite
        defaultPosition: 'bottom-16 md:bottom-24 right-1',
        sessionStorageDir: 'right',
        textRotation: '',
        useWritingMode: true
      }
    };
    return configs[direction];
  }

  // Générer le HTML du bouton
  render() {
    const config = NavButton.getDirectionConfig(this.direction);
    if (!config) {
      throw new Error(`Direction "${this.direction}" not supported. Use: up, down, left, right`);
    }

    // Position: soit custom, soit par défaut selon la direction
    const position = this.options.position === 'center' 
      ? config.defaultPosition 
      : this.options.position;

    // Si iconOnly est défini, on affiche seulement l'icône
    if (this.options.iconOnly) {
      const chevronElement = `<span class="w-10 h-10 md:w-12 md:h-12 nav-btn-arrow nav-btn-arrow--${this.direction} flex items-center justify-center">${config.chevron}</span>`;
      const containerClasses = `flex items-center justify-center text-gold hover:text-white transition-colors duration-200`;
      
      return `
        <a href="${this.href}" 
           onclick="sessionStorage.setItem('vt-dir','${config.sessionStorageDir}')" 
           class="pointer-events-auto absolute ${position} nav-btn nav-btn--${this.direction} ${this.options.className}"
           aria-label="${this.options.ariaLabel}">
          <span class="${containerClasses}">
            ${chevronElement}
          </span>
        </a>
      `;
    }

    // Version complète avec texte (pour les autres pages)
    const chevronElement = `<span class="w-6 h-6 md:w-8 md:h-8 nav-btn-arrow nav-btn-arrow--${this.direction} flex items-center justify-center">${config.chevron}</span>`;
    const textElement = `<span class="text-xl md:text-2xl font-medium ${config.textRotation}">${this.text}</span>`;

    // Ordre selon la direction
    const content = config.textOrder === 'chevron-first'
      ? `${chevronElement}${textElement}`
      : `${textElement}${chevronElement}`;

    // Espacement selon l'alignement
    const gap = config.alignment === 'flex-row' ? 'gap-2' : 'gap-1';

    // Classes spéciales pour les boutons verticaux
    const containerClasses = config.useWritingMode 
      ? `nav-btn-vertical-container text-gold hover:text-white transition-colors duration-200`
      : `flex ${config.alignment} items-center justify-center ${gap} text-gold hover:text-white transition-colors duration-200`;

    return `
      <a href="${this.href}" 
         onclick="sessionStorage.setItem('vt-dir','${config.sessionStorageDir}')" 
         class="pointer-events-auto absolute ${position} nav-btn nav-btn--${this.direction} ${this.options.className}"
         aria-label="${this.options.ariaLabel}">
        <span class="${containerClasses}">
          ${content}
        </span>
      </a>
    `;
  }

  // Méthode statique pour créer rapidement un bouton
  static create(direction, text, href, options = {}) {
    const button = new NavButton(direction, text, href, options);
    return button.render();
  }

  // Méthodes helper pour créer des boutons spécifiques
  static createUp(text = 'LRB', href = '/lrb.html', options = {}) {
    return NavButton.create('up', text, href, options);
  }

  static createDown(text = 'Back', href = '/index.html', options = {}) {
    return NavButton.create('down', text, href, options);
  }

  static createLeft(text = 'Contact', href = '/contact.html', options = {}) {
    return NavButton.create('left', text, href, options);
  }

  static createRight(text = 'Concepts', href = '/concepts.html', options = {}) {
    return NavButton.create('right', text, href, options);
  }

  // Générer une navigation complète (pour page d'accueil)
  static createHomeNavigation() {
    return [
      NavButton.createUp('LRB', '/lrb.html', { iconOnly: true }),
      NavButton.createRight('Concepts', '/concepts.html', { iconOnly: true }), 
      NavButton.createLeft('Contact', '/contact.html', { iconOnly: true })
    ].join('');
  }

  // Générer un bouton retour (pour pages satellites)
  static createBackButton(customPosition = null) {
    const options = customPosition ? { position: customPosition } : {};
    return NavButton.createDown('Back', '/index.html', options);
  }
}

export default NavButton;